import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Clock, MapPin, Navigation, Bell, FolderOpen, CloudOff, CalendarOff, Lock, Coffee, Play, ChevronRight, CheckCircle2, Sun, Footprints, FileText, Timer } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { easerGet, fmtTime, toISODate } from '../../lib/easerApi';
import { subscribeDolibarr } from '../../lib/dolibarrStatus';
import { subscribeQueue } from '../../lib/offlineQueue';
import { EaserSatisfactionCard } from './EaserSatisfactionCard';
import { EaserQualityCard } from './EaserQualityCard';
import { InstallAppCard } from '../InstallAppCard';
import { EaserWeather } from './EaserWeather';
import { EaserMoodCard } from './EaserMoodCard';
import { EaserWeekCard } from './EaserWeekCard';
import { STATUS } from './EventDetailDialog';
import { useHomeLayout } from '../../lib/homeLayout';

const mapsLink = (client) => (client && (client.address || client.town)
  ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${client.address || ''} ${client.zip || ''} ${client.town || ''}`)}`
  : null);

const greeting = () => { const h = new Date().getHours(); return h < 18 ? 'Bonjour' : 'Bonsoir'; };
const todayLabel = () => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

/**
 * Accueil: the day's important reminders — ongoing / paused prestation, next intervention, unread notifications,
 * new documents, offline queue, leave requests under review, client satisfaction.
 * Widget order / visibility is personal (GRH › Paramètres › Organisation de l'accueil).
 */
export function EaserHome({ user, active, locked, paused, pauseOverdue, overdue, unreadNotifications, newDocs, onUnauthorized, onGo, onResume, resuming }) {
  const [today, setToday] = useState(null); // today's planning items
  const [leaves, setLeaves] = useState(null);
  const [queue, setQueue] = useState([]);
  const [error, setError] = useState(false);
  const [stale, setStale] = useState(false); // data shown comes from the phone's last successful load
  const [dolibarrDown, setDolibarrDown] = useState(false);
  const { layout } = useHomeLayout();
  // Today's planning is kept on the phone so the home page stays useful offline / when Dolibarr is down.
  const cacheKey = `easer_home_cache_${user?.id || 'me'}_${toISODate(new Date())}`;

  const load = useCallback(async () => {
    const day = toISODate(new Date());
    try {
      const [pl, lv] = await Promise.all([
        easerGet('/api/easer/planning', { start: day, end: day }),
        easerGet('/api/easer/leaves').catch(() => ({ data: { items: [] } })),
      ]);
      setToday(pl.data.items || []);
      setLeaves(lv.data.items || []);
      setError(false);
      setStale(false);
      try { localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), items: pl.data.items || [], leaves: lv.data.items || [] })); } catch { /* quota */ }
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { cached = null; }
      setToday((t) => (t && t.length ? t : cached?.items || []));
      setLeaves((l) => (l && l.length ? l : cached?.leaves || []));
      setStale(!!cached?.items);
      setError(true);
    }
  }, [onUnauthorized, cacheKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('easer:clock-changed', load);
    window.addEventListener('online', load);
    window.addEventListener('easer:dolibarr-restored', load);
    return () => { window.removeEventListener('easer:clock-changed', load); window.removeEventListener('online', load); window.removeEventListener('easer:dolibarr-restored', load); };
  }, [load]);
  useEffect(() => subscribeDolibarr((st) => setDolibarrDown(!!st.degraded)), []);
  useEffect(() => subscribeQueue(setQueue), []);

  const now = Date.now();
  const interventions = (today || []).filter((e) => e.type_code === 'AC_INT' && !e.is_tract);
  const tracts = (today || []).filter((e) => e.is_tract);
  const done = interventions.filter((e) => e.status === 'done').length;
  const next = interventions.find((e) => e.status !== 'done' && (!e.end || new Date(e.end).getTime() > now - 15 * 60000) && e.id !== active?.id && e.id !== paused?.event_id)
    || interventions.find((e) => e.status !== 'done' && e.id !== active?.id && e.id !== paused?.event_id);
  const pendingLeaves = (leaves || []).filter((l) => l.status === 'pending');
  const nextLeave = (leaves || []).filter((l) => l.status === 'approved' && new Date(l.end) >= new Date(toISODate(new Date()))).sort((a, b) => (a.start < b.start ? -1 : 1))[0];

  const reminders = [];
  if (locked && active) reminders.push({ id: 'active', tone: overdue ? 'danger' : 'accent', icon: Lock, title: `Prestation en cours${active.since ? ` depuis ${fmtTime(active.since)}` : ''} : ${active.label}`, text: overdue ? 'Fin prévue dépassée de plus de 30 min : pointez la fin de prestation.' : 'Pensez à pointer la fin de prestation une fois terminée.', action: 'Fin de prestation', go: () => onGo('planning'), testId: 'home-reminder-active' });
  if (paused && !locked) reminders.push({ id: 'paused', tone: pauseOverdue ? 'danger' : 'accent', icon: Coffee, title: `Prestation en pause depuis ${fmtTime(paused.started_at)} : ${paused.label}`, text: pauseOverdue ? 'Pause de plus de 30 min : reprenez puis clôturez la prestation.' : 'Reprenez la prestation pour continuer, puis pointez sa fin.', action: 'Reprendre', go: onResume, busy: resuming, icon2: Play, testId: 'home-reminder-paused' });
  if (unreadNotifications > 0) reminders.push({ id: 'notifs', tone: 'primary', icon: Bell, title: `${unreadNotifications} notification${unreadNotifications > 1 ? 's' : ''} de l'agence non lue${unreadNotifications > 1 ? 's' : ''}`, text: 'Messages et challenges à découvrir dans GRH › Notifications.', action: 'Lire', go: () => onGo('grh', 'notifications'), testId: 'home-reminder-notifications' });
  if (newDocs > 0) reminders.push({ id: 'docs', tone: 'primary', icon: FolderOpen, title: `${newDocs} nouveau${newDocs > 1 ? 'x' : ''} document${newDocs > 1 ? 's' : ''}`, text: 'Bulletin de paie ou document déposé par l\'agence.', action: 'Voir', go: () => onGo('documents'), testId: 'home-reminder-documents' });
  if (queue.length > 0) reminders.push({ id: 'queue', tone: 'accent', icon: CloudOff, title: `${queue.length} envoi${queue.length > 1 ? 's' : ''} en attente de réseau`, text: 'Pointages, comptes-rendus ou frais enregistrés sur le téléphone : ils partiront automatiquement.', testId: 'home-reminder-queue' });
  if (pendingLeaves.length > 0) reminders.push({ id: 'leaves', tone: 'muted', icon: CalendarOff, title: `${pendingLeaves.length} demande${pendingLeaves.length > 1 ? 's' : ''} de congés en cours de validation`, text: pendingLeaves.map((l) => `${new Date(l.start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} → ${new Date(l.end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`).join(' · '), action: 'Suivre', go: () => onGo('grh', 'conges'), testId: 'home-reminder-leaves' });

  const toneClass = { danger: 'border-destructive/50 bg-destructive/10 text-destructive', accent: 'border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]', primary: 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))]', muted: 'border-border bg-card text-muted-foreground' };

  const widgets = {
    mood: <EaserMoodCard key="mood" onUnauthorized={onUnauthorized} />,
    satisfaction: <EaserSatisfactionCard key="satisfaction" onUnauthorized={onUnauthorized} />,
    qualite: <EaserQualityCard key="qualite" user={user} onUnauthorized={onUnauthorized} />,
    reminders: (
      <section key="reminders" aria-labelledby="home-reminders-title" data-testid="home-reminders">
        <h3 id="home-reminders-title" className="text-sm font-semibold text-muted-foreground mb-2">Rappels du jour</h3>
        {today === null && leaves === null ? <Skeleton className="h-16 rounded-xl" /> : reminders.length === 0 ? (
          <Card className="p-4 flex items-center gap-3 border-[hsl(var(--brand-sage))]/40" data-testid="home-reminders-empty">
            <span className="h-10 w-10 rounded-xl bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0"><CheckCircle2 className="h-5 w-5" /></span>
            <div><p className="font-medium">Rien d'urgent pour le moment</p><p className="text-sm text-muted-foreground">Aucune notification, document ou envoi en attente.</p></div>
          </Card>
        ) : (
          <div className="space-y-2">
            {reminders.map((r) => (
              <div key={r.id} className={`rounded-xl border px-3 py-2.5 text-sm flex items-start gap-2 ${toneClass[r.tone]}`} role={r.tone === 'danger' ? 'alert' : 'status'} data-testid={r.testId}>
                <r.icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${r.tone === 'danger' ? 'animate-pulse' : ''}`} />
                <div className="min-w-0 flex-1 text-foreground">
                  <p className="font-semibold leading-snug">{r.title}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{r.text}</p>
                </div>
                {r.action && r.go && (
                  <Button size="sm" variant="outline" className="h-9 flex-shrink-0 bg-card" onClick={r.go} disabled={r.busy} data-testid={`${r.testId}-action`}>
                    {r.icon2 ? <r.icon2 className="h-4 w-4 mr-1" /> : null}{r.action}<ChevronRight className="h-4 w-4 ml-0.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    ),
    next: (
      <section key="next" data-testid="home-next">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">Prochaine intervention
          {stale && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--brand-apricot))]" data-testid="home-stale-data"><CloudOff className="h-3 w-3" /> dernier chargement</span>}
        </h3>
        {today === null ? <Skeleton className="h-32 rounded-xl" /> : next ? (
          <Card className="p-4 border-border shadow-sm" data-testid="home-next-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold leading-snug" data-testid="home-next-label">{next.label}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3.5 w-3.5" /> {next.fullday ? 'Journée entière' : `${fmtTime(next.start)} – ${fmtTime(next.end)}`}</p>
                {next.intervention && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><FileText className="h-3 w-3" /> <span className="font-mono">{next.intervention.ref}</span></p>}
              </div>
              <Badge className={`${(STATUS[next.status] || STATUS.planned).className} flex-shrink-0`}>{(STATUS[next.status] || STATUS.planned).label}</Badge>
            </div>
            {next.client && (
              <div className="rounded-lg bg-[hsl(var(--secondary))]/60 p-3 text-sm space-y-1 mt-3">
                <p className="font-medium">{next.client.name}</p>
                {(next.client.address || next.client.town) && <p className="text-muted-foreground flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {next.client.address}{next.client.address && ', '}{next.client.zip} {next.client.town}</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-3">
              {mapsLink(next.client) ? (
                <Button asChild variant="outline" className="h-11" data-testid="home-next-maps"><a href={mapsLink(next.client)} target="_blank" rel="noopener noreferrer"><Navigation className="h-4 w-4 mr-1" /> Itinéraire</a></Button>
              ) : <span />}
              <Button className="h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" onClick={() => onGo('planning')} data-testid="home-next-open"><Timer className="h-4 w-4 mr-1" /> Pointer / détails</Button>
            </div>
          </Card>
        ) : (
          <Card className="p-5 text-center" data-testid="home-next-empty">
            <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="font-medium">{interventions.length ? 'Toutes les interventions du jour sont terminées' : 'Aucune intervention prévue aujourd\'hui'}</p>
            {error && <p className="text-xs text-muted-foreground mt-1" data-testid="home-planning-unavailable">{dolibarrDown ? 'Dolibarr est indisponible : le planning du jour s\'affichera à son retour.' : 'Planning indisponible pour le moment (hors ligne ?).'}</p>}
          </Card>
        )}
      </section>
    ),
    summary: (
      <React.Fragment key="summary">
        {today !== null && (interventions.length > 0 || tracts.length > 0 || nextLeave) && (
          <div className="grid grid-cols-3 gap-2" data-testid="home-day-summary">
            <Card className="p-3 text-center"><p className="text-2xl font-semibold leading-none" data-testid="home-count-interventions">{interventions.length}</p><p className="text-[11px] text-muted-foreground mt-1">intervention{interventions.length > 1 ? 's' : ''}</p></Card>
            <Card className="p-3 text-center"><p className="text-2xl font-semibold leading-none text-[hsl(var(--brand-sage))]" data-testid="home-count-done">{done}</p><p className="text-[11px] text-muted-foreground mt-1">terminée{done > 1 ? 's' : ''}</p></Card>
            <Card className="p-3 text-center">
              {tracts.length > 0
                ? <><p className="text-2xl font-semibold leading-none flex items-center justify-center gap-1"><Footprints className="h-5 w-5" />{tracts.length}</p><p className="text-[11px] text-muted-foreground mt-1">tracts</p></>
                : nextLeave
                  ? <><p className="text-sm font-semibold leading-tight">{new Date(nextLeave.start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p><p className="text-[11px] text-muted-foreground mt-1">prochain congé</p></>
                  : <><p className="text-2xl font-semibold leading-none">{interventions.length - done}</p><p className="text-[11px] text-muted-foreground mt-1">à faire</p></>}
            </Card>
          </div>
        )}
      </React.Fragment>
    ),
    week: <EaserWeekCard key="week" onUnauthorized={onUnauthorized} />,
    weather: <EaserWeather key="weather" onUnauthorized={onUnauthorized} />,
    install: <InstallAppCard key="install" portal="easer" />,
  };

  return (
    <div className="space-y-4" data-testid="easer-home">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Sun className="h-3.5 w-3.5" /> <span className="first-letter:uppercase">{todayLabel()}</span></p>
          <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight" data-testid="home-greeting">{greeting()} {user?.firstname || user?.name}</h2>
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => onGo('planning')} data-testid="home-open-planning"><CalendarDays className="h-4 w-4 mr-1" /> Planning</Button>
      </div>

      {layout.order.filter((id) => !layout.hidden.includes(id)).map((id) => widgets[id])}
    </div>
  );
}
